import { z } from 'zod';
import {
  camadaSchema,
  comSinalDe,
  sinalDeTabela,
  validarSinal,
  validarTotal,
  valoresDaCamada,
  type ValoresDaCadeira,
} from './camadas.js';
import { formatarBRL } from './dinheiro.js';
import { enderecoSchema, ENDERECO_VAZIO } from './endereco.js';
import { etapaSchema, marcoSchema } from './etapas.js';
import { mascararTelefone } from './mascaras.js';
import { apenasDigitos, validarDocumento, validarTelefone } from './validacao.js';

export const tipoPessoaSchema = z.enum(['pj', 'pf']);
export type TipoPessoa = z.infer<typeof tipoPessoaSchema>;

/**
 * Titulares gravados antes de `contato` virar dois campos trazem o texto livre
 * "(11) 98765-4321 · fulano@empresa.com" numa string só. Aqui ele é separado na leitura,
 * e a próxima gravação da ficha persiste o resultado.
 *
 * O que não couber nos dois campos é descartado: o campo sempre foi "Telefone e e-mail",
 * e guardar sobra de pontuação em lugar nenhum não ajuda ninguém. O telefone só é
 * aproveitado se for telefone de verdade — um número quebrado que passasse daqui faria a
 * própria validação do schema recusar a ficha inteira na leitura, e a ficha ficaria
 * inacessível pelo cockpit.
 */
function separarContatoAntigo(entrada: unknown): unknown {
  if (typeof entrada !== 'object' || entrada === null) return entrada;

  const titular = entrada as Record<string, unknown>;
  const contato = titular.contato;
  if (typeof contato !== 'string' || contato === '') return entrada;
  if (titular.telefone !== undefined || titular.email !== undefined) return entrada;

  const email = /[^\s·,;/|]+@[^\s·,;/|]+\.[^\s·,;/|]+/.exec(contato)?.[0] ?? '';
  const digitos = apenasDigitos(contato.replace(email, ''));

  return { ...titular, telefone: validarTelefone(digitos) ? digitos : '', email };
}

/**
 * Titular nomeado da cadeira. Só existe em cadeira de pessoa jurídica: até três
 * C-levels ou diretores, com direito de uso individual e não simultâneo (Termo, cl. II).
 */
export const titularSchema = z.preprocess(
  separarContatoAntigo,
  z.object({
    nome: z.string().trim().max(120).default(''),
    cargo: z.string().trim().max(80).default(''),
    cpf: z
      .string()
      .trim()
      .max(20)
      .default('')
      .refine((v) => v === '' || validarDocumento(v), { message: 'CPF inválido' }),
    /** Só dígitos, como todo telefone do sistema — a máscara é da tela. */
    telefone: z
      .string()
      .trim()
      .max(24)
      .default('')
      .refine((v) => v === '' || validarTelefone(v), { message: 'Telefone inválido' }),
    email: z
      .union([z.literal(''), z.string().trim().email({ message: 'E-mail inválido' })])
      .default(''),
  }),
);
export type Titular = z.infer<typeof titularSchema>;

/**
 * Telefone e e-mail do titular numa linha — é assim que o Termo os imprime, na coluna que
 * o instrumento aprovado já tinha, e é assim que eles entram na impressão dos dados.
 *
 * Compor a partir dos dois campos, em vez de gravar a linha pronta, é o que faz um Termo
 * emitido antes da separação continuar batendo com a ficha: o texto sai idêntico ao
 * `contato` que estava lá, e o documento não aparece desatualizado sem motivo.
 */
export function contatoDoTitular(titular: Pick<Titular, 'telefone' | 'email'>): string {
  return [mascararTelefone(titular.telefone), titular.email].filter(Boolean).join(' · ');
}

export const MAX_TITULARES = 3;

/** Campos que o cockpit envia ao criar ou atualizar um candidato. */
export const candidatoEntradaSchema = z
  .object({
    tipo: tipoPessoaSchema.default('pj'),
    /** Razão social (PJ) ou nome completo (PF). */
    razao: z.string().trim().max(160).default(''),
    /** CNPJ (PJ) ou CPF (PF), com ou sem máscara. */
    doc: z
      .string()
      .trim()
      .max(20)
      .default('')
      .refine((v) => v === '' || validarDocumento(v), { message: 'CPF ou CNPJ inválido' }),
    /**
     * Estruturado, não texto livre: o CEP preenche quase tudo e o que sobra para a pessoa
     * é número e complemento. Ver packages/shared/src/endereco.ts.
     */
    endereco: enderecoSchema.default(ENDERECO_VAZIO),
    telefone: z
      .string()
      .trim()
      .max(24)
      .default('')
      .refine((v) => v === '' || validarTelefone(v), { message: 'Telefone inválido' }),
    /** Representante legal — só se aplica a pessoa jurídica. */
    representante: z.string().trim().max(120).default(''),
    email: z
      .union([z.literal(''), z.string().trim().email({ message: 'E-mail inválido' })])
      .default(''),
    titulares: z.array(titularSchema).max(MAX_TITULARES).default([]),
    camada: camadaSchema.default('movimento'),
    /**
     * Número da cadeira. A faixa depende da camada e é validada no servidor, porque o
     * patch pode trazer só um dos dois campos — ver `validarNumeroDaCadeira`.
     */
    numero: z.string().trim().max(3).default(''),
    /**
     * Sinal negociado com o candidato, **em centavos**. Ausente (ou `null`) significa
     * "vale a tabela": os `PERCENTUAL_SINAL`% calculados sobre o investimento anual.
     *
     * Junto com `totalNegociado`, é o que a operação digita — e são só esses dois: o saldo
     * é sempre o resto da subtração, recalculado por `valoresVigentes`, daí não existir
     * campo de saldo aqui. Três campos editáveis para a mesma conta é o caminho para uma
     * ficha em que sinal + saldo não fecha o investimento.
     *
     * Editável só até o link do sinal existir (ver `restricoesDeEdicao`): depois disso o
     * valor está no gateway, e mudá-lo aqui faria a ficha divergir do que foi cobrado.
     */
    sinalNegociado: z.number().int().positive().nullish(),
    /**
     * Investimento anual negociado com o candidato, **em centavos**. Ausente (ou `null`)
     * significa "vale a tabela": o valor da camada, ou o congelado no contrato depois de a
     * Carta sair.
     *
     * Desconto e acréscimo são decisão comercial e acontecem — o que não pode acontecer é
     * a ficha guardar um total que não conversa com o sinal e com o saldo. Por isso só o
     * total e o sinal são digitáveis: o saldo é sempre o resto da subtração, e o sinal,
     * quando não foi negociado, acompanha o total pelos `PERCENTUAL_SINAL`% da tabela.
     * Tudo recalculado por `valoresVigentes`.
     *
     * Editável na mesma janela do sinal — até o link do sinal existir (ver
     * `restricoesDeEdicao`). Depois disso, o gateway já cobra sobre estes valores.
     */
    totalNegociado: z.number().int().positive().nullish(),
    observacoes: z.string().trim().max(2000).default(''),
  })
  .strict();
export type CandidatoEntrada = z.infer<typeof candidatoEntradaSchema>;

/** Atualização parcial — o cockpit salva a ficha inteira, mas a API aceita PATCH. */
export const candidatoPatchSchema = candidatoEntradaSchema.partial().strict();
export type CandidatoPatch = z.infer<typeof candidatoPatchSchema>;

/**
 * Valores congelados no momento em que a Carta de Intenções foi emitida.
 *
 * A tabela de preços da comunidade pode mudar — renovação, segunda turma, correção. O que
 * o Proponente assinou, não. A partir da emissão, documentos e cobranças leem daqui e
 * nunca mais da tabela viva.
 */
export const valoresContratadosSchema = z.object({
  total: z.number().int().nonnegative(),
  sinal: z.number().int().nonnegative(),
  saldo: z.number().int().nonnegative(),
  camada: camadaSchema,
  congeladoEm: z.string().datetime(),
});
export type ValoresContratados = z.infer<typeof valoresContratadosSchema>;

export const candidatoSchema = candidatoEntradaSchema.extend({
  candidatoId: z.string(),
  /**
   * Referência do documento, "CI 007/2026". Gerada pelo servidor por contador atômico:
   * identificador de instrumento contratual não se digita, e duas fichas com a mesma
   * referência tornariam a conciliação impossível.
   */
  referencia: z.string(),
  etapa: etapaSchema,
  /** Marcos da esteira: id do marco → data ISO em que ocorreu. */
  marcos: z.record(marcoSchema, z.string().datetime()).default({}),
  /** Preenchido na emissão da Carta. Antes disso, vale a tabela vigente. */
  valoresContratados: valoresContratadosSchema.optional(),
  /**
   * Pendência financeira aberta: sinal estornado, chargeback ou cobrança vencida.
   * Enquanto existir, a cadeira segue bloqueada e a operação precisa decidir.
   */
  pendenciaFinanceira: z.string().max(300).optional(),
  /** Motivo registrado na recusa da curadoria ou na desistência. */
  motivoEncerramento: z.string().max(500).optional(),
  criadoEm: z.string().datetime(),
  criadoPor: z.string(),
  atualizadoEm: z.string().datetime(),
  atualizadoPor: z.string(),
});
export type Candidato = z.infer<typeof candidatoSchema>;

/**
 * Valores que valem para este candidato: o congelado no contrato, se já houver, senão a
 * tabela vigente — e, por cima dos dois, o investimento anual e o sinal negociados, quando
 * existirem. **Toda** exibição de valor, emissão de documento e cobrança passa por aqui —
 * chamar `valoresDaCamada` direto sobre um candidato é o caminho para imprimir número
 * diferente do que foi assinado.
 *
 * Os negociados vencem o congelado de propósito: eles só podem mudar enquanto o link do
 * sinal não existe, e nessa janela o que vale é o que a operação combinou, não o que a
 * Carta emitida imprimiu. A Carta desatualizada é sinalizada na esteira e reemitida — é
 * exatamente para isso que existe o versionamento dos documentos.
 */
export function valoresVigentes(
  candidato: Pick<Candidato, 'camada' | 'valoresContratados' | 'sinalNegociado' | 'totalNegociado'>,
): ValoresDaCadeira {
  const base = candidato.valoresContratados ?? valoresDaCamada(candidato.camada);
  const total = candidato.totalNegociado ?? base.total;

  /*
   * Mexer no investimento anual sem renegociar o sinal mantém o **percentual**, não o
   * valor: um total cortado pela metade com o sinal parado dobraria a entrada de 5% para
   * 10% sem ninguém ter combinado isso. Quando o total é o da base, o sinal da base é que
   * vale — ele pode ter sido congelado num percentual que não é o da tabela.
   */
  const sinalDaBase = total === base.total ? base.sinal : sinalDeTabela(total);

  return comSinalDe(total, candidato.sinalNegociado ?? sinalDaBase);
}

export interface ValorNegociadoInvalido {
  /** Campo a apontar na tela — é onde o operador digitou. */
  campo: 'totalNegociado' | 'sinalNegociado';
  motivo: string;
}

export interface ValoresDoPatch {
  /**
   * O que gravar em cada campo. `undefined` = o patch não mexe nele; `null` = volta para o
   * valor de tabela (o da camada, ou o congelado no contrato).
   */
  totalNegociado?: number | null;
  sinalNegociado?: number | null;
  /** Preenchido quando os valores pedidos não fecham — a escrita deve ser recusada. */
  problema: ValorNegociadoInvalido | null;
}

/**
 * Concilia investimento anual e sinal negociados de um patch com a ficha vigente.
 *
 * Os dois andam juntos e não dá para conferir um sem o outro: o sinal é validado contra o
 * total que vai valer depois do patch, e o total é validado contra o sinal que vai
 * continuar valendo. Três coisas acontecem aqui, e valem para qualquer cliente da API,
 * não só para o cockpit:
 *
 *  1. **Trocar de camada retabela os dois.** Um total de R$ 120.000 e um sinal de R$ 6.000
 *     combinados numa cadeira Movimento não querem dizer nada numa Círculo — e os dois
 *     passariam pela validação sem ninguém perceber que a cadeira saiu por um terço do
 *     preço. Quem quiser renegociar na camada nova manda os campos no mesmo patch.
 *  2. **A base é o total que vai valer**: o congelado no contrato quando a Carta já saiu,
 *     e o da camada nova quando o patch troca de camada.
 *  3. **Reduzir o total abaixo de um sinal já negociado é recusado no campo do total**,
 *     que é onde o operador está digitando. Retabelar o sinal em silêncio ali apagaria
 *     uma negociação que ninguém pediu para desfazer.
 */
export function conciliarValoresNegociados(
  atual: Pick<Candidato, 'camada' | 'valoresContratados' | 'sinalNegociado' | 'totalNegociado'>,
  patch: Pick<CandidatoPatch, 'camada' | 'sinalNegociado' | 'totalNegociado'>,
): ValoresDoPatch {
  const trocouDeCamada = patch.camada !== undefined && patch.camada !== atual.camada;
  const resultado: ValoresDoPatch = { problema: null };

  /** O que fica gravado no campo depois deste patch, já considerando a retabelagem. */
  function conciliar(campo: 'totalNegociado' | 'sinalNegociado'): number | null {
    const pedido = patch[campo];
    if (pedido !== undefined) {
      resultado[campo] = pedido ?? null;
      return pedido ?? null;
    }
    if (trocouDeCamada && atual[campo] != null) {
      resultado[campo] = null;
      return null;
    }
    return atual[campo] ?? null;
  }

  const totalNegociado = conciliar('totalNegociado');
  const sinalNegociado = conciliar('sinalNegociado');

  // Sem os negociados, para pegar a base que vale — a congelada ou a da camada nova.
  const { total: base } = valoresVigentes({
    camada: patch.camada ?? atual.camada,
    valoresContratados: atual.valoresContratados,
    sinalNegociado: null,
    totalNegociado: null,
  });
  const total = totalNegociado ?? base;

  const problemaDoTotal = totalNegociado == null ? null : validarTotal(totalNegociado);
  if (problemaDoTotal) {
    return { ...resultado, problema: { campo: 'totalNegociado', ...problemaDoTotal } };
  }

  if (sinalNegociado != null) {
    const problemaDoSinal = validarSinal(total, sinalNegociado);
    if (problemaDoSinal) {
      /*
       * Se o patch não mexeu no sinal, quem estourou o limite foi o total que acabou de ser
       * digitado — e a recusa precisa aparecer no campo em que a pessoa está, com o caminho
       * para sair do impasse: ajustar o sinal no mesmo salvamento.
       */
      return patch.sinalNegociado === undefined
        ? {
            ...resultado,
            problema: {
              campo: 'totalNegociado',
              motivo:
                'O investimento anual precisa ser maior que o sinal negociado ' +
                `(${formatarBRL(sinalNegociado)}). Ajuste o sinal no mesmo salvamento.`,
            },
          }
        : { ...resultado, problema: { campo: 'sinalNegociado', ...problemaDoSinal } };
    }
  }

  return resultado;
}

/** A camada não pode mudar depois de o valor virar contrato. */
export function podeTrocarDeCamada(candidato: Pick<Candidato, 'valoresContratados'>): boolean {
  return !candidato.valoresContratados;
}

/**
 * Campos obrigatórios para emitir cobrança: sem eles o Asaas recusa a criação do cliente.
 * Retorna a lista de pendências em linguagem de negócio (vazia = pode cobrar).
 *
 * O endereço entrou na lista quando o link passou a ser um checkout: o gateway exige CEP,
 * logradouro, número e bairro do pagador e recusa a criação sem eles. Conferir aqui existe
 * para o operador ler "Falta: CEP, bairro" em vez de "O Asaas recusou a operação: O campo
 * postalCode deve ser informado" — a mesma recusa, dita por quem sabe o que ela significa.
 *
 * Complemento fica de fora: nem todo endereço tem, e o gateway não pede.
 */
export function pendenciasParaCobranca(
  c: Pick<Candidato, 'razao' | 'doc' | 'email' | 'endereco'>,
): string[] {
  const faltando: string[] = [];
  if (!c.razao) faltando.push('nome ou razão social');
  if (!c.doc) faltando.push('CPF ou CNPJ');
  else if (!validarDocumento(c.doc)) faltando.push('CPF ou CNPJ válido');
  if (!c.email) faltando.push('e-mail');

  const endereco = c.endereco;
  if (!endereco?.cep) faltando.push('CEP');
  if (!endereco?.logradouro) faltando.push('logradouro');
  if (!endereco?.numero) faltando.push('número do endereço');
  if (!endereco?.bairro) faltando.push('bairro');

  return faltando;
}
